import React, { useRef, useEffect, useState } from "react";
import { getMedia } from "Services/media";
import A4TicketCardRebars from "./ticket-card-rebars";
import A4TicketCardSpecialNets from "./ticket-card-special-net";
import A4TicketCardStandardNets from "./ticket-card-standard-net";
import A4TicketCardPiles from "./ticket-card-piles";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchBusinesses } from "Redux/middlwares/business";
import standardLogo from "../../../../assets/img/ticket-print-pops/standard-logo.png";
import { REBAR_SUBCLASS_TYPE } from "../../../../constants/rebar-constants";
import { NET_SUBCLASS_TYPE } from "../../../../constants/net-constants";
import { PILE_SUBCLASS_TYPE } from "Constants/pile-constants";

import "./index.scss";

const PreviewTicketPdf = ({
  pageSize,
  loader,
  orderNo,
  handleRefs,
  totalPages,
  selectedRowCount,
  showStandardLogo,
}: any) => {
  const pageRef: any = useRef([]);
  const dispatch = useAppDispatch();

  const [companyLogo, setCompanyLogo] = useState<string | null>(null);

  const userData = useAppSelector((state) => state.user.userData);

  const userBusinesses = useAppSelector(
    (state) => state.business.userBusinesses
  );

  const completeOrderDetails = useAppSelector(
  (state) => state.supplier.completeOrderDetails
);

const sectionHeaders =
  completeOrderDetails?.section_headers ??
  completeOrderDetails?.headers ??
  [];

const siteStreet = String(
  completeOrderDetails?.site_address || ""
).trim();

const siteCity = String(
  completeOrderDetails?.delivery_city ||
  completeOrderDetails?.project_city ||
  ""
).trim();

const formattedSiteAddress = [
  siteStreet ? `רח' ${siteStreet}` : "",
  siteCity,
]
  .filter(Boolean)
  .join(", ");

  
const getSectionTitle = (
  item: any,
  sectionType: string
): string => {
  const sectionItems =
    sectionType === REBAR_SUBCLASS_TYPE
      ? currentOrderRebars
      : sectionType === NET_SUBCLASS_TYPE
      ? currentOrderNets
      : sectionType === PILE_SUBCLASS_TYPE
      ? currentOrderPiles
      : [];

  const typeHeaders = sectionHeaders
    .filter(
      (header: any) =>
        header.section_type === sectionType
    )
    .sort(
      (a: any, b: any) =>
        Number(a.position) - Number(b.position) ||
        Number(a.id) - Number(b.id)
    );

  let activeTitle = "";
  let itemIndex = 0;
  let headerIndex = 0;

  const totalRows =
    sectionItems.length + typeHeaders.length;

  for (
    let position = 0;
    position < totalRows;
    position += 1
  ) {
    const header = typeHeaders[headerIndex];

    if (
      header &&
      Number(header.position) === position
    ) {
      activeTitle = String(
        header.title ?? ""
      ).trim();

      headerIndex += 1;
      continue;
    }

    const currentItem = sectionItems[itemIndex];

    if (!currentItem) {
      continue;
    }

    if (
      Number(currentItem.id) === Number(item.id)
    ) {
      return activeTitle;
    }

    itemIndex += 1;
  }

  return "";
};

useEffect(() => {
  if (userData && userBusinesses.length === 0) {
    dispatch(fetchBusinesses());
  }
}, [userData, userBusinesses.length, dispatch]);

useEffect(() => {
  let cancelled = false;

const loadCompanyLogo = async () => {
  if (userBusinesses.length === 0) return;

  const logoId = userBusinesses[0]?.logo;

  if (!logoId) {
    setCompanyLogo("");
    return;
  }

  try {
    const response: any = await Promise.race([
      getMedia(logoId),

      new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error("Logo request timeout")),
          1500,
        );
      }),
    ]);

    if (cancelled) return;

    const logoUrl =
      response?.data?.file_field ??
      response?.file_field ??
      "";

    setCompanyLogo(
      logoUrl
        ? logoUrl.replace(
            "https://easybar-s3.s3.amazonaws.com",
            "https://app.easybar.co.il",
          )
        : "",
    );
  } catch (error) {
    console.warn("Company logo was not loaded:", error);

    if (!cancelled) {
      setCompanyLogo("");
    }
  }
};

  loadCompanyLogo();

  return () => {
    cancelled = true;
  };
}, [userBusinesses]);

  const currentOrderRebars = useAppSelector(
    (state) => state.supplier.ticketOrderDetails
  );
  const currentOrderNets = useAppSelector(
    (state) => state.supplier.ticketOrderNets
  );
  const currentOrderPiles = useAppSelector(
    (state) => state.supplier.ticketOrderPiles
  );


useEffect(() => {
  if (companyLogo === null) return;

  handleRefs(pageRef);
}, [companyLogo]);

  const printpage = (data: any, refIndex: any, ticketIndex: any) => {
    return (
      <>
        <div
          style={{
            height: pageSize + "px",
            width: "1728px",
            justifyContent: loader ? "normal" : "",
          }}
          ref={(el) => {
            pageRef.current[refIndex] = el;
          }}
        >
          <div className="pdf-grid-container">
            {data.map((rebar, index) => {
              currentItemCount.set(
                rebar.id,
                currentItemCount.get(rebar.id) + 1
              );

              if (rebar.tool.subclass_type === REBAR_SUBCLASS_TYPE) {
                return (
                  <A4TicketCardRebars
                    key={"ticket " + index}
                    rebar={rebar}
                    orderNo={orderNo}
                    ticketIndex={rebar.row_number}
                    printTotalCount={itemCounts.get(rebar.id)}
                    printCurrentCount={currentItemCount.get(rebar.id)}
                    showStandardLogo={showStandardLogo}
                    companyLogo={companyLogo}
                    siteAddress={formattedSiteAddress}
                    sectionTitle={getSectionTitle(
                      rebar,
                      REBAR_SUBCLASS_TYPE
                    )}
                  />
                );
              } else if (
                rebar.tool.subclass_type === NET_SUBCLASS_TYPE &&
                rebar.tool.standard_net
              ) {
                return (
                  <A4TicketCardStandardNets
                    key={"ticket " + index}
                    net={rebar}
                    orderNo={orderNo}
                    ticketIndex={rebar.row_number}
                    printTotalCount={itemCounts.get(rebar.id)}
                    printCurrentCount={currentItemCount.get(rebar.id)}
                    showStandardLogo={showStandardLogo}
                    companyLogo={companyLogo}
                    siteAddress={formattedSiteAddress}
                    sectionTitle={getSectionTitle(
                      rebar,
                      NET_SUBCLASS_TYPE
                    )}
                  />
                );
              } else if (
                rebar.tool.subclass_type === NET_SUBCLASS_TYPE &&
                !rebar.tool.standard_net
              ) {
                return (
                  <A4TicketCardSpecialNets
                    key={"ticket " + index}
                    net={rebar}
                    orderNo={orderNo}
                    ticketIndex={rebar.row_number}
                    printTotalCount={itemCounts.get(rebar.id)}
                    printCurrentCount={currentItemCount.get(rebar.id)}
                    showStandardLogo={showStandardLogo}
                    companyLogo={companyLogo}
                    siteAddress={formattedSiteAddress}
                    sectionTitle={getSectionTitle(
                      rebar,
                      NET_SUBCLASS_TYPE
                    )}
                  />
                );
              } else if (rebar.tool.subclass_type === PILE_SUBCLASS_TYPE) {
                return (
                  <A4TicketCardPiles
                    key={"ticket_piles " + index}
                    pile={rebar}
                    orderNo={orderNo}
                    ticketIndex={rebar.row_number}
                    printTotalCount={itemCounts.get(rebar.id)}
                    printCurrentCount={currentItemCount.get(rebar.id)}
                    showStandardLogo={showStandardLogo}
                    companyLogo={companyLogo}
                    siteAddress={formattedSiteAddress}
                    sectionTitle={getSectionTitle(
                      rebar,
                      PILE_SUBCLASS_TYPE
                  )}
                  />
                );
              } else {
                return <></>;
              }
            })}
          </div>
        </div>
      </>
    );
  };

  const allRows = [
    ...currentOrderRebars,
    ...currentOrderNets,
    ...currentOrderPiles,
  ];
  // Create a map of item IDs to counts
  const itemCounts = new Map();
  const currentItemCount = new Map();
  for (const { itemId, countt } of selectedRowCount) {
    itemCounts.set(itemId, countt);
    currentItemCount.set(itemId, 0);
  }

  // Create a new array with items repeated based on count
  const repeatedItems = [];
  for (const row of allRows) {
    const count = itemCounts.get(row.id) || 0;
    for (let i = 0; i < count; i++) {
      repeatedItems.push(row);
    }
  }

  if (repeatedItems && repeatedItems.length) {
    const SLICE = 8;
    const dataSlice: any = [];
    let initialSlice = 0;
    if (repeatedItems.length < 9) {
      dataSlice.push(repeatedItems.slice(initialSlice, initialSlice + SLICE));
    } else {
      while (repeatedItems.length > initialSlice) {
        dataSlice.push(repeatedItems.slice(initialSlice, initialSlice + SLICE));
        initialSlice = initialSlice + SLICE;
      }
    }

    return (
      <>
        {dataSlice.map((data: any, index: any) =>
          printpage(data, index, index)
        )}
      </>
    );
  }
  return <></>;
};

export default PreviewTicketPdf;